import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import { useDocumentsV2Service } from 'services/documentsV2';
import {
  CreateDocumentV2Request,
  DocumentV2Kind,
  DocumentV2Record,
} from 'types/documentsV2';
import styles from './DocumentModuleV2.module.scss';

interface DocumentModuleV2Props {
  kind: DocumentV2Kind;
  title: string;
  counterpartyLabel: string;
}

function emptyItem() {
  return {
    description: '',
    quantity: 1,
    unitPrice: 0,
    unitOfMeasure: '',
    discount: 0,
    taxType: 'V' as const,
  };
}

function DocumentModuleV2({
  kind,
  title,
  counterpartyLabel,
}: DocumentModuleV2Props) {
  const service = useDocumentsV2Service(kind);
  const [records, setRecords] = useState<DocumentV2Record[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [includeVoided, setIncludeVoided] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [form, setForm] = useState<CreateDocumentV2Request>({
    date: new Date().toISOString().slice(0, 10),
    referenceNumber: '',
    counterpartyName: '',
    description: '',
    documentType: 'SI',
    currency: 'PHP',
    sellerTin: '',
    sellerBranchCode: '',
    sellerRegisteredName: '',
    sellerAddress: '',
    buyerTin: '',
    buyerAddress: '',
    vatableSales: 0,
    vatAmount: 0,
    vatExemptSales: 0,
    zeroRatedSales: 0,
    totalDiscount: 0,
    lineItems: [emptyItem()],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await service.list(includeVoided);
      setRecords(result);
    } catch (error) {
      setToast({
        type: 'error',
        message:
          error instanceof Error ? error.message : 'Failed to load records',
      });
    } finally {
      setLoading(false);
    }
  }, [service, includeVoided]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalFromForm = useMemo(
    () =>
      form.lineItems.reduce(
        (sum, item) =>
          sum + Number(item.quantity || 0) * Number(item.unitPrice || 0),
        0,
      ),
    [form.lineItems],
  );

  const createRecord = async () => {
    if (!form.referenceNumber.trim() || !form.counterpartyName.trim()) {
      setToast({
        type: 'error',
        message: 'Reference and counterparty are required',
      });
      return;
    }
    if (
      form.lineItems.length === 0 ||
      form.lineItems.some((item) => !item.description.trim())
    ) {
      setToast({
        type: 'error',
        message: 'At least one line item with description is required',
      });
      return;
    }
    setSaving(true);
    try {
      await service.create(form);
      setToast({ type: 'success', message: `${title} record created` });
      setForm({
        date: new Date().toISOString().slice(0, 10),
        referenceNumber: '',
        counterpartyName: '',
        description: '',
        documentType: 'SI',
        currency: 'PHP',
        sellerTin: '',
        sellerBranchCode: '',
        sellerRegisteredName: '',
        sellerAddress: '',
        buyerTin: '',
        buyerAddress: '',
        vatableSales: 0,
        vatAmount: 0,
        vatExemptSales: 0,
        zeroRatedSales: 0,
        totalDiscount: 0,
        lineItems: [emptyItem()],
      });
      await load();
    } catch (error) {
      setToast({
        type: 'error',
        message:
          error instanceof Error ? error.message : 'Failed to create record',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{title} (V2)</h1>
        <RefreshButton onClick={load} loading={loading} />
      </div>

      <div className={styles.formCard}>
        <h2>Create New</h2>
        <div className={styles.grid}>
          <input
            value={form.date}
            type="date"
            onChange={(e) => {
              setForm((prev) => ({ ...prev, date: e.target.value }));
            }}
          />
          <input
            placeholder="Reference #"
            value={form.referenceNumber}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, referenceNumber: e.target.value }));
            }}
          />
          <input
            placeholder={counterpartyLabel}
            value={form.counterpartyName}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                counterpartyName: e.target.value,
              }));
            }}
          />
          <input
            placeholder="Description (optional)"
            value={form.description ?? ''}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, description: e.target.value }));
            }}
          />
          <select
            value={form.documentType ?? 'SI'}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                documentType: e.target.value as 'SI' | 'OR' | 'DM' | 'CM',
              }));
            }}
          >
            <option value="SI">SI</option>
            <option value="OR">OR</option>
            <option value="DM">DM</option>
            <option value="CM">CM</option>
          </select>
          <input
            placeholder="Currency (e.g. PHP)"
            value={form.currency ?? ''}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, currency: e.target.value }));
            }}
          />
          <input
            placeholder="Seller TIN (optional)"
            value={form.sellerTin ?? ''}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, sellerTin: e.target.value }));
            }}
          />
          <input
            placeholder="Seller Branch Code (optional)"
            value={form.sellerBranchCode ?? ''}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                sellerBranchCode: e.target.value,
              }));
            }}
          />
          <input
            placeholder="Seller Registered Name (optional)"
            value={form.sellerRegisteredName ?? ''}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                sellerRegisteredName: e.target.value,
              }));
            }}
          />
          <input
            placeholder="Seller Address (optional)"
            value={form.sellerAddress ?? ''}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, sellerAddress: e.target.value }));
            }}
          />
          <input
            placeholder="Buyer TIN (optional)"
            value={form.buyerTin ?? ''}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, buyerTin: e.target.value }));
            }}
          />
          <input
            placeholder="Buyer Address (optional)"
            value={form.buyerAddress ?? ''}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, buyerAddress: e.target.value }));
            }}
          />
          <input
            type="number"
            min={0}
            step="0.01"
            placeholder="Vatable Sales"
            value={form.vatableSales ?? 0}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                vatableSales: Number(e.target.value),
              }));
            }}
          />
          <input
            type="number"
            min={0}
            step="0.01"
            placeholder="VAT Amount"
            value={form.vatAmount ?? 0}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                vatAmount: Number(e.target.value),
              }));
            }}
          />
          <input
            type="number"
            min={0}
            step="0.01"
            placeholder="VAT Exempt Sales"
            value={form.vatExemptSales ?? 0}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                vatExemptSales: Number(e.target.value),
              }));
            }}
          />
          <input
            type="number"
            min={0}
            step="0.01"
            placeholder="Zero Rated Sales"
            value={form.zeroRatedSales ?? 0}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                zeroRatedSales: Number(e.target.value),
              }));
            }}
          />
          <input
            type="number"
            min={0}
            step="0.01"
            placeholder="Total Discount"
            value={form.totalDiscount ?? 0}
            onChange={(e) => {
              setForm((prev) => ({
                ...prev,
                totalDiscount: Number(e.target.value),
              }));
            }}
          />
        </div>
        <div className={styles.items}>
          {form.lineItems.map((item, idx) => (
            <div key={`item-${idx}`} className={styles.itemRow}>
              <input
                placeholder="Line item description"
                value={item.description}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    lineItems: prev.lineItems.map((li, i) =>
                      i === idx ? { ...li, description: e.target.value } : li,
                    ),
                  }));
                }}
              />
              <input
                type="number"
                min={0}
                step="0.01"
                placeholder="Qty"
                value={item.quantity}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    lineItems: prev.lineItems.map((li, i) =>
                      i === idx
                        ? { ...li, quantity: Number(e.target.value) }
                        : li,
                    ),
                  }));
                }}
              />
              <input
                type="number"
                min={0}
                step="0.01"
                placeholder="Unit price/cost"
                value={item.unitPrice}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    lineItems: prev.lineItems.map((li, i) =>
                      i === idx
                        ? { ...li, unitPrice: Number(e.target.value) }
                        : li,
                    ),
                  }));
                }}
              />
              <input
                placeholder="UoM"
                value={item.unitOfMeasure ?? ''}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    lineItems: prev.lineItems.map((li, i) =>
                      i === idx ? { ...li, unitOfMeasure: e.target.value } : li,
                    ),
                  }));
                }}
              />
              <input
                type="number"
                min={0}
                step="0.01"
                placeholder="Discount"
                value={item.discount ?? 0}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    lineItems: prev.lineItems.map((li, i) =>
                      i === idx
                        ? { ...li, discount: Number(e.target.value) }
                        : li,
                    ),
                  }));
                }}
              />
              <select
                value={item.taxType ?? 'V'}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    lineItems: prev.lineItems.map((li, i) =>
                      i === idx
                        ? { ...li, taxType: e.target.value as 'V' | 'E' | 'Z' }
                        : li,
                    ),
                  }));
                }}
              >
                <option value="V">V</option>
                <option value="E">E</option>
                <option value="Z">Z</option>
              </select>
              <Button
                variant="outline"
                onClick={() => {
                  setForm((prev) => ({
                    ...prev,
                    lineItems: prev.lineItems.filter((_, i) => i !== idx),
                  }));
                }}
              >
                Remove
              </Button>
            </div>
          ))}
          <div className={styles.actions}>
            <Button
              variant="outline"
              onClick={() => {
                setForm((prev) => ({
                  ...prev,
                  lineItems: [...prev.lineItems, emptyItem()],
                }));
              }}
            >
              Add Line Item
            </Button>
            <div className={styles.total}>
              Total: {totalFromForm.toFixed(2)}
            </div>
            <Button onClick={() => void createRecord()} disabled={saving}>
              {saving ? 'Saving...' : 'Create'}
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.filterBar}>
        <label>
          <input
            type="checkbox"
            checked={includeVoided}
            onChange={(e) => {
              setIncludeVoided(e.target.checked);
            }}
          />
          Include voided
        </label>
      </div>

      <div className={styles.tableWrap}>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Reference</th>
              <th>{counterpartyLabel}</th>
              <th>Status</th>
              <th>Total</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 && (
              <tr>
                <td colSpan={6} className={styles.emptyCell}>
                  No records yet.
                </td>
              </tr>
            )}
            {records.map((record) => (
              <tr key={record.id}>
                <td>{new Date(record.date).toLocaleDateString()}</td>
                <td>{record.referenceNumber}</td>
                <td>{record.buyerName ?? record.supplierName ?? '-'}</td>
                <td>{record.isVoided ? 'Voided' : record.paymentStatus}</td>
                <td>{record.totalAmount.toFixed(2)}</td>
                <td>
                  {!record.isVoided ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        void service
                          .voidRecord(record.id, 'Voided from V2 module')
                          .then(load)
                          .catch((error) => {
                            setToast({
                              type: 'error',
                              message:
                                error instanceof Error
                                  ? error.message
                                  : 'Failed to void record',
                            });
                          });
                      }}
                    >
                      Void
                    </Button>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {
            setToast(null);
          }}
        />
      )}
    </div>
  );
}

export default DocumentModuleV2;
